import { Controller } from "@hotwired/stimulus"

const FILE_TYPE_ICONS = {
  "pdf": "📄",
  "doc": "📝", "docx": "📝",
  "xls": "📊", "xlsx": "📊", "csv": "📊",
  "ppt": "📎", "pptx": "📎",
  "zip": "🗜️", "rar": "🗜️", "7z": "🗜️", "tar": "🗜️", "gz": "🗜️",
  "txt": "📃", "md": "📃", "log": "📃",
  "json": "{ }", "xml": "📋", "yml": "📋", "yaml": "📋",
  "mp4": "🎬", "mov": "🎬", "avi": "🎬", "webm": "🎬",
  "mp3": "🎵", "wav": "🎵", "ogg": "🎵",
}

function getFileIcon(filename) {
  const ext = filename.split(".").pop().toLowerCase()
  return FILE_TYPE_ICONS[ext] || "📎"
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default class extends Controller {
  static targets = [
    "description", "preview", "labelsInput", "writeTab", "previewTab",
    "fileInput", "fileCount", "filePreviewArea", "filePreviewList"
  ]

  connect() {
    this.previewVisible = false
    this.selectedFiles = []
    this.syncSelectedLabels()
  }

  togglePreview() {
    this.previewVisible = !this.previewVisible

    if (this.hasWriteTabTarget && this.hasPreviewTabTarget) {
      this.writeTabTarget.classList.toggle("hidden", this.previewVisible)
      this.previewTabTarget.classList.toggle("hidden", !this.previewVisible)
    }

    if (this.previewVisible && this.hasDescriptionTarget && this.hasPreviewTarget) {
      this.previewTarget.textContent = this.descriptionTarget.value
    }
  }

  toggleLabel(event) {
    event.preventDefault()
    event.currentTarget.classList.toggle("selected")
    this.updateLabelsInput()
  }

  submit(event) {
    event.preventDefault()

    const form = event.target
    const formData = new FormData(form)
    
    if (this.hasFileInputTarget) {
      formData.delete(this.fileInputTarget.name)
      this.selectedFiles.forEach(file => {
        formData.append(this.fileInputTarget.name, file)
      })
    }

    fetch(form.action, {
      method: form.method.toUpperCase(),
      headers: {
        "X-CSRF-Token": this.csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: formData
    })
      .then(response => response.text())
      .then(html => {
        Turbo.renderStreamMessage(html)
        this.closeModal()
      })
  }

  closeModal() {
    const modalBackdrop = document.getElementById("issue-modal-backdrop")
    if (modalBackdrop?.contains(this.element)) {
      const controller = this.application.getControllerForElementAndIdentifier(modalBackdrop, "modal")
      controller?.close()
      return
    }

    const drawerBackdrop = document.getElementById("issue-drawer-backdrop")
    if (drawerBackdrop?.contains(this.element)) {
      const controller = this.application.getControllerForElementAndIdentifier(drawerBackdrop, "drawer")
      controller?.close()
    }
  }

  updateLabelsInput() {
    if (!this.hasLabelsInputTarget) return

    const labels = this.element.querySelectorAll(".label-chip.selected")
    this.labelsInputTarget.value = Array.from(labels).map(chip => chip.dataset.label).join(",")
  }

  syncSelectedLabels() {
    if (!this.hasLabelsInputTarget) return

    const selected = this.labelsInputTarget.value.split(",").map(label => label.trim()).filter(Boolean)
    this.element.querySelectorAll(".label-chip").forEach(chip => {
      chip.classList.toggle("selected", selected.includes(chip.dataset.label))
    })
  }

  previewFiles() {
    if (!this.hasFileInputTarget) return
    
    const newFiles = Array.from(this.fileInputTarget.files)
    this.selectedFiles.push(...newFiles)

    this._renderFilePreviews()
  }

  removeFile(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.selectedFiles.splice(index, 1)
    this._renderFilePreviews()
  }

  _renderFilePreviews() {
    const list = this.filePreviewListTarget
    const area = this.filePreviewAreaTarget

    list.innerHTML = ""

    if (this.selectedFiles.length === 0) {
      area.classList.add("hidden")
      this.fileCountTarget.textContent = ""
      return
    }

    area.classList.remove("hidden")
    this.fileCountTarget.textContent = `${this.selectedFiles.length} file${this.selectedFiles.length === 1 ? "" : "s"} selected`

    this.selectedFiles.forEach((file, index) => {
      if (file.type.startsWith("image/")) {
        this._renderImagePreview(file, index, list)
      } else {
        this._renderFileCard(file, index, list)
      }
    })
  }

  _createRemoveBtn(index) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.index = index
    btn.dataset.action = "issue-form#removeFile"
    btn.className = "absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center text-xs leading-none shadow-sm transition-colors cursor-pointer"
    btn.innerHTML = "×"
    return btn
  }

  _renderImagePreview(file, index, container) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const wrapper = document.createElement("div")
      wrapper.className = "relative group/thumb"

      const img = document.createElement("img")
      img.src = e.target.result
      img.className = "w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
      img.alt = file.name

      const nameTag = document.createElement("span")
      nameTag.className = "block text-[10px] text-gray-400 dark:text-gray-500 truncate w-20 mt-0.5"
      nameTag.textContent = file.name

      wrapper.appendChild(img)
      wrapper.appendChild(this._createRemoveBtn(index))
      wrapper.appendChild(nameTag)
      container.appendChild(wrapper)
    }
    reader.readAsDataURL(file)
  }

  _renderFileCard(file, index, container) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"

    const icon = document.createElement("span")
    icon.className = "text-base shrink-0"
    icon.textContent = getFileIcon(file.name)

    const info = document.createElement("div")
    info.className = "min-w-0 flex-1"

    const name = document.createElement("span")
    name.className = "block text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[140px]"
    name.textContent = file.name

    const size = document.createElement("span")
    size.className = "block text-[10px] text-gray-400 dark:text-gray-500"
    size.textContent = formatFileSize(file.size)

    info.appendChild(name)
    info.appendChild(size)
    wrapper.appendChild(icon)
    wrapper.appendChild(info)
    wrapper.appendChild(this._createRemoveBtn(index))
    container.appendChild(wrapper)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content
  }
}
